import React from 'react';

// Hoooks
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Utils
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';

import { Box, Stepper, Step, StepLabel, Typography, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// Components
import CampaignDetailsSkeleton from './CampaignDetailsSkeleton';

// Styles
import './styles.scss';

const getStatusClassName = (status) => {
  switch (status) {
    case 'Processing':
    case 'Completed':
    case 'Mailed':
      return 'inProcess';
    case 'Scheduled':
      return 'scheduled';
    case 'Canceled':
    case 'Failed':
      return 'canceled';
    default:
      return 'statusChip';
  }
};

const CampaignDetails = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const campaignData = useSelector((state) => state.ordersReducers.OrderDetailsCampaigns);
  const campaignDetails = campaignData?.automation?.config?.steps;
  const campaignOrderDetails = campaignData?.orders;

  const url = window.location.pathname;
        
  const handleClick = (rowId) => {
    navigate(`${url.substring(0, url.lastIndexOf('/') + 1)}${rowId}`);
  };

  const columns = [
    {
      field: 'orderNumber',
      headerName: 'Order Number',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader',
      cellClassName: 'dataGridCell',
      renderCell: (params) => {
        return(
          <Typography>
            {params.row.orderNumber}
          </Typography>
        )
      }
    },
    {
      field: 'scheduledDate',
      headerName: 'Schedule Date',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader',
      cellClassName: 'dataGridCell',
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 180,
      flex: 1,
      headerClassName: 'dataGridHeader statusColumnHeader',
      cellClassName: 'dataGridCell statusColumnCell',
      renderCell: (params) => {
        return (
          <Chip
            label={params.value}
            size="small"
            className={`statusChip ${getStatusClassName(params.value)}`}
          />
        );
      },
    },
  ];
  
  const activeStatus = ['Processing', 'Mailed', 'Completed'];

  const rows = campaignOrderDetails?.length
    ? campaignOrderDetails.map((campaign, idx) => ({
      id: idx,
      orderNumber: campaign.id,
      scheduledDate: dateFormat(campaign.deliveryDate, true, true),
      status: campaign.status,
    }))
    : [];

  const steps = campaignDetails?.length && campaignOrderDetails?.length
    ? campaignDetails.reduce((acc, campaign, index) => {
      if (campaign.type === 'delay') return acc;

      const stepOrder = campaignOrderDetails.find(
        (order) => order.meta?.automationStepIndex === index
      );

      acc.push({
        id: acc.length,
        label: campaign.label,
        subtitle: `Order #${stepOrder?.id}`,
        status: stepOrder?.status,
        description: campaign.description,
        orderId: stepOrder?.id,
      });

      return acc;
    }, [])
    : [];

  // Custom step icon component
  const CustomStepIcon = ({ completed, icon, step }) => {
    const isActive = activeStatus.includes(step.status);
    return (
      <div className={`stepIcon ${isActive ? 'active' : ''} ${completed ? 'completed' : ''}`}>
        <span className="stepNumber">{icon}</span>
      </div>
    );
  };

  if (campaignData === null) {
    return <CampaignDetailsSkeleton />;
  }

  if (!campaignDetails?.length && !campaignOrderDetails?.length) {
    return (
      <Box className="noCampaignsContainer">
        <Typography variant="h6" className="sectionTitle">{MESSAGES.ORDERS.DETAIL.CAMPAIGN.NO_CAMPAIGNS}</Typography>
      </Box>
    )
  }

  return (
    <Box className="campaignDetailsContainer">
      <Box className="campaignContent">
        {/* Left side - Stepper */}
        <Box className="stepperContainer">
          <Typography variant="h6" className="sectionTitle">
            Campaign Progress
          </Typography>

          <Stepper orientation="vertical" className="customStepper">
            {steps.map((step, index) => (
              <Step key={step.id} className="customStep">
                <StepLabel
                  StepIconComponent={(props) => (
                    <CustomStepIcon {...props} completed={step.orderId === id} icon={index + 1} step={step} />
                  )}
                  className="customStepLabel"
                >
                  <Box className="stepLabelContent">
                    <Typography className="stepTitle">{step.label}</Typography>
                    <Typography className="stepSubtitle">{step.subtitle}</Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Right side - DataGrid Table */}
        <Box className="tableContainerCampaignOrder">
          <DataGrid
            rows={rows}
            columns={columns}
            hideFooter
            rowHeight={56}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            disableColumnSorting
            onRowClick={(params) => handleClick(params.row.orderNumber)}
            getRowClassName={(params) => {
              return String(params.row.orderNumber) === String(id) ? 'rowWithId' : '';
            }}
            classes={{
              virtualScroller: `virtualScroller ${rows.length > 4 ? 'normalVirtualScroller' : 'minHeightVirtualScroller'}`,
            }}
            className="customDataGrid"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignDetails;
